import type { MetaSdk, WhatsAppConfiguration } from './metaSdk'

export type WhatsAppFinishType =
  | 'FINISH'
  | 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'

export type WhatsAppSignupResult = {
  code: string
  wabaId: string
  phoneNumberId: string
  finishType: WhatsAppFinishType
}

export const SIGNUP_ERROR =
  'We couldn’t complete WhatsApp signup. Refresh this page and try again.'
const INCOMPLETE_RESULT =
  'We didn’t receive all the signup details. Refresh this page and try again.'
const RELOAD_REQUIRED = 'Refresh this page to start a new WhatsApp signup.'
const EMBEDDED_SIGNUP_MESSAGE_TYPE = 'WA_EMBEDDED_SIGNUP'
const interruptedSignups = new WeakSet<MetaSdk>()

type FinishData = {
  waba_id: string
  phone_number_id: string
}
type EmbeddedSignupMessage = {
  type: typeof EMBEDDED_SIGNUP_MESSAGE_TYPE
} & (
  | { event: WhatsAppFinishType; data?: FinishData }
  | {
      event: 'CANCEL'
      data?:
        | { current_step: string }
        | {
            error_code: string
            error_message: string
            session_id: string
            timestamp: string
          }
    }
  | { event: 'ERROR'; data?: unknown }
  | { event: `FINISH_${string}`; data?: Partial<FinishData> }
)

function isMetaOrigin(origin: string): boolean {
  try {
    const url = new URL(origin)
    return (
      url.protocol === 'https:' &&
      (url.hostname === 'facebook.com' ||
        url.hostname.endsWith('.facebook.com'))
    )
  } catch {
    return false
  }
}

export function startWhatsAppSignup({
  sdk,
  configuration,
  onResult,
  onError,
  onIncomplete,
}: {
  sdk: MetaSdk
  configuration: WhatsAppConfiguration
  onResult: (result: WhatsAppSignupResult) => void
  onError: (message: string, requiresReload?: boolean) => void
  onIncomplete: () => void
}): () => void {
  let active = true
  let code: string | undefined
  let assets: Omit<WhatsAppSignupResult, 'code'> | undefined
  let timer: number | undefined

  function dispose(): void {
    active = false
    window.clearTimeout(timer)
    window.removeEventListener('message', onMessage)
    code = undefined
    assets = undefined
  }

  function fail(message: string): void {
    interruptedSignups.add(sdk)
    dispose()
    onError(message, true)
  }

  function completeOrWait(): void {
    if (code && assets) {
      const result = { code, ...assets }
      dispose()
      onResult(result)
    } else if (timer === undefined) {
      timer = window.setTimeout(() => fail(INCOMPLETE_RESULT), 30_000)
    }
  }

  function onMessage(event: MessageEvent<unknown>): void {
    if (
      !active ||
      !isMetaOrigin(event.origin) ||
      typeof event.data !== 'string'
    )
      return
    let message: EmbeddedSignupMessage
    try {
      message = JSON.parse(event.data)
    } catch {
      return
    }
    if (
      message?.type !== EMBEDDED_SIGNUP_MESSAGE_TYPE ||
      typeof message.event !== 'string'
    )
      return

    switch (message.event) {
      case 'ERROR':
        fail(SIGNUP_ERROR)
        return
      case 'CANCEL':
        if (!message.data || 'error_code' in message.data) {
          fail(SIGNUP_ERROR)
        } else {
          dispose()
          onIncomplete()
        }
        return
      case 'FINISH':
      case 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING': {
        if (!message.data) {
          fail(INCOMPLETE_RESULT)
          return
        }
        const { waba_id: wabaId, phone_number_id: phoneNumberId } = message.data
        if (!wabaId || !phoneNumberId) {
          fail(INCOMPLETE_RESULT)
          return
        }
        assets = { wabaId, phoneNumberId, finishType: message.event }
        completeOrWait()
        return
      }
      default:
        if (message.event.startsWith('FINISH')) {
          fail('Buffer supports standard WhatsApp signup only.')
        }
    }
  }

  if (interruptedSignups.has(sdk)) {
    dispose()
    onError(RELOAD_REQUIRED, true)
    return dispose
  }

  window.addEventListener('message', onMessage)
  try {
    // The host app can initialize this shared SDK with a different Meta app.
    sdk.init({
      appId: configuration.metaAppId,
      version: configuration.graphApiVersion,
      autoLogAppEvents: false,
      xfbml: false,
    })
    sdk.login(
      (response) => {
        if (!active) return
        const loginCode = response.authResponse?.code
        if (loginCode?.trim()) {
          code = loginCode
          completeOrWait()
        } else {
          dispose()
          onIncomplete()
        }
      },
      {
        config_id: configuration.embeddedSignupConfigId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
        },
      },
    )
  } catch {
    if (active) fail(SIGNUP_ERROR)
  }
  return (): void => {
    // FB.login exposes no popup handle to close or correlate after unmount.
    if (active) interruptedSignups.add(sdk)
    dispose()
  }
}
